import { useContext, useMemo } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import _ from "lodash";
import cx from "classnames";

import Thumbnail from "src/components/thumbnail";
import Text from "src/components/text";

import { Asset } from "src/types/assets";
import { DealRoomActionPlanContext } from "../../../../context";
import { FileService } from "src/utils/file.service";
import { assetMetadata } from "src/utils/dealRoom.helper";

import styles from "./styles.module.scss";

const AssetCard = ({ asset, onView }) => {
  const fileType = useMemo(() => {
    return FileService.getFileTypeDealRoomAsset(asset.metadata);
  }, [asset]);

  const handleClick = () => {
    if (onView) {
      onView(asset);
    }
  };

  return (
    <div
      className={cx(styles.entity, styles.asset)}
      style={{ width: 230 }}
      onClick={handleClick}
    >
      <div className={styles.inner}>
        <Thumbnail
          className={styles.icon}
          media={asset?.icon}
          width={44}
          height={40}
          defaultImage="/images/assets/default_asset.jpg"
        />
        <div className={styles.details}>
          <Text
            className={styles.name}
            text={_.get(asset, "name") ?? "N/A"}
            tooltip={{ placement: "right" }}
          />
          <div className={styles.meta}>
            <p>{assetMetadata(asset)}</p>
          </div>
        </div>
        <div className={styles.actions}>
          <i className={cx(styles.viewIcon, "paperflite-redirect")} />
        </div>
      </div>
    </div>
  );
};

interface AssetsPopover {
  dealRoomId: string;
  stageId: string;
  actionId: string;
  selectedAssets: string[];
  canEditAction: boolean;
  onClick: () => void;
}

const getButtonText = (assets: Asset[]) => {
  const numberOfAttachments = _.size(assets);

  if (numberOfAttachments === 0) {
    return "Attach";
  }

  return `${numberOfAttachments} Attachment${numberOfAttachments > 1 ? "s" : ""}`;
};

const AssetsPopover = ({
  stageId,
  actionId,
  selectedAssets,
  isNewAction,
  canEditAction,
  isActionCompleted,
  onClick = () => {},
}) => {
  const { state, toggleEditAction, toggleAddAssetsSlider } = useContext(
    DealRoomActionPlanContext,
  );

  const editMode = _.get(state, "editMode");
  const editingItem = _.get(state, "editingItem");

  const buttonText = getButtonText(selectedAssets);

  const handleAddAssets = () => {
    if (!editMode) {
      return;
    }

    toggleEditAction(stageId, actionId, isActionCompleted);
    document.body.click(); // Close the popover
    toggleAddAssetsSlider(true, stageId, actionId, isNewAction, selectedAssets);
  };

  const handleViewAsset = (asset: Asset) => {
    window.open(`/assets/${asset.id}`, "_blank");
  };

  return (
    <OverlayTrigger
      trigger="click"
      placement="left"
      flip
      rootClose
      overlay={
        !editMode || _.get(editingItem, "id") === actionId ? (
          <Popover id={`popover-${actionId}`} className={styles.popover}>
            <Popover.Body>
              <div className={styles.container}>
                <div className={styles.headingContainer}>
                  <p className={styles.heading}>Attached documents</p>
                  {(editMode && !isActionCompleted && _.size(selectedAssets) > 0) && (
                    <div
                      className={styles.addAssetsContainer}
                      onClick={handleAddAssets}
                    >
                      <i className={cx(styles.icon, "paperflite-edit")} />
                      <p className={styles.text}>Edit</p>
                    </div>
                  )}
                </div>
                {_.size(selectedAssets) === 0 && (
                  <div className={styles.emptyAssets}>
                    <p className={styles.subtext}>No assets attached yet!</p>
                    <div
                      className={styles.addAssetsContainer}
                      onClick={handleAddAssets}
                    >
                      <i className={cx(styles.icon, "paperflite-add")} />
                      <p className={styles.text}>Add assets</p>
                    </div>
                  </div>
                )}
                {_.size(selectedAssets) > 0 && (
                  <div className={styles.assetsContainer}>
                    {_.map(selectedAssets, (asset: Asset, index: number) => (
                      <AssetCard
                        asset={asset}
                        onView={handleViewAsset}
                        key={`viewAssetCard-${index}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </Popover.Body>
          </Popover>
        ) : (
          <></>
        )
      }
    >
      <div
        className={cx(styles.actionButton, {
          [styles.disabled]:
            !canEditAction ||
            isActionCompleted ||
            (editingItem && _.get(editingItem, "id") !== actionId),
        })}
        onClick={onClick}
      >
        <i className={cx(styles.icon, "paperflite-attachment")} />
        <p className={styles.buttonText}>{buttonText}</p>
      </div>
    </OverlayTrigger>
  );
};

export default AssetsPopover;
